import type { AddOnDefinition, AddOnKey, SampleTrack } from "./types";
import deLocale from "../../theme/locales/de.json";
import enLocale from "../../theme/locales/en.default.json";
import esLocale from "../../theme/locales/es.json";
import frLocale from "../../theme/locales/fr.json";
import itLocale from "../../theme/locales/it.json";
import jaLocale from "../../theme/locales/ja.json";

export type LocaleCode = "en" | "es" | "fr" | "it" | "de" | "ja";

export const defaultLocale: LocaleCode = "en";

export const localeOptions: Array<{ code: LocaleCode; label: string; languageTag: string }> = [
  { code: "en", label: "English", languageTag: "en" },
  { code: "es", label: "Español", languageTag: "es" },
  { code: "fr", label: "Français", languageTag: "fr" },
  { code: "it", label: "Italiano", languageTag: "it" },
  { code: "de", label: "Deutsch", languageTag: "de" },
  { code: "ja", label: "日本語", languageTag: "ja" },
];

const localeCodes = new Set(localeOptions.map((locale) => locale.code));

export function isLocaleCode(value: string): value is LocaleCode {
  return localeCodes.has(value as LocaleCode);
}

export function localeToLanguageTag(locale: LocaleCode): string {
  return localeOptions.find((option) => option.code === locale)?.languageTag || "en";
}

export const genres = [
  "Surprise me",
  "Pop",
  "Acoustic",
  "Country",
  "Rock",
  "R&B / Soul",
  "Hip-Hop / Rap",
  "Folk",
  "Indie",
  "Jazz",
  "Blues",
  "Classical",
  "Gospel",
  "Electronic / Dance",
  "Latin",
  "Reggae",
  "Musical Theatre",
  "Other",
];

export const addOns: AddOnDefinition[] = [
  {
    key: "instrumental",
    handle: import.meta.env.VITE_SHOPIFY_INSTRUMENTAL_HANDLE || "instrumental",
    label: "Instrumental",
    type: "Instrumental",
    approvedPrice: "$7.95 USD",
    description: "A downloadable instrumental MP3 for this song.",
  },
  {
    key: "lyric_video",
    handle: import.meta.env.VITE_SHOPIFY_LYRIC_VIDEO_HANDLE || "youtube-video-with-lyrics",
    label: "Lyric Video",
    type: "Lyric Video",
    approvedPrice: "$13.95 USD",
    description: "A downloadable video using this song, synchronized lyrics, and cover artwork.",
  },
  {
    key: "commercial_licence",
    handle: import.meta.env.VITE_SHOPIFY_COMMERCIAL_LICENCE_HANDLE || "commercial-licence",
    label: "Commercial Licence",
    type: "Commercial Licence",
    approvedPrice: "$29.95 USD",
    description: "Commercial-use rights plus a guaranteed high-quality WAV master.",
  },
];

type TuneTailorLocale = typeof enLocale.tunetailor;
type TrackKey = keyof TuneTailorLocale["audio"]["tracks"];
type OccasionKey = keyof TuneTailorLocale["occasion_items"];
type ProcessStepKey = keyof TuneTailorLocale["process"]["steps"];
type IncludeKey = keyof TuneTailorLocale["product"]["includes"];
type FaqKey = keyof TuneTailorLocale["faq"]["items"];

const locales: Record<LocaleCode, TuneTailorLocale> = {
  en: enLocale.tunetailor,
  es: esLocale.tunetailor,
  fr: frLocale.tunetailor,
  it: itLocale.tunetailor,
  de: deLocale.tunetailor,
  ja: jaLocale.tunetailor,
};

const languageLabels: Record<LocaleCode, string> = {
  en: enLocale.localization.language_label,
  es: esLocale.localization.language_label,
  fr: frLocale.localization.language_label,
  it: itLocale.localization.language_label,
  de: deLocale.localization.language_label,
  ja: jaLocale.localization.language_label,
};

const sampleAssets: Array<{
  key: TrackKey;
  duration: string;
  audioUrl: string;
  artworkUrl: string;
}> = [
  {
    key: "track_1",
    duration: "3:34",
    audioUrl: "https://cdn.shopify.com/s/files/1/0949/0851/4633/files/1st_Year_Anniversary.mp3?v=1752471992",
    artworkUrl: "https://cdn.shopify.com/s/files/1/0949/0851/4633/files/jason_and_mary_artwork.png",
  },
  {
    key: "track_2",
    duration: "3:17",
    audioUrl: "https://cdn.shopify.com/s/files/1/0949/0851/4633/files/Graduation_Tribute_to_Sarah.mp3?v=1752473133",
    artworkUrl: "https://cdn.shopify.com/s/files/1/0949/0851/4633/files/graduation_tribute_to_sarah_artwork.png",
  },
  {
    key: "track_3",
    duration: "3:27",
    audioUrl: "https://cdn.shopify.com/s/files/1/0949/0851/4633/files/Welcome_Home_Dad.mp3?v=1752471911",
    artworkUrl: "https://cdn.shopify.com/s/files/1/0949/0851/4633/files/welcome_home_dad_artwork.jpg",
  },
  {
    key: "track_4",
    duration: "3:15",
    audioUrl: "https://cdn.shopify.com/s/files/1/0949/0851/4633/files/Story_of_Friendship.mp3?v=1752471993",
    artworkUrl: "https://cdn.shopify.com/s/files/1/0949/0851/4633/files/story_of_friendship_artwork.png",
  },
  {
    key: "track_5",
    duration: "2:22",
    audioUrl: "https://cdn.shopify.com/s/files/1/0949/0851/4633/files/Gigi_Loves_You.mp3?v=1756065255",
    artworkUrl: "https://cdn.shopify.com/s/files/1/0949/0851/4633/files/gigi_loves_you_artwork.png",
  },
  {
    key: "track_6",
    duration: "2:19",
    audioUrl: "https://cdn.shopify.com/s/files/1/0949/0851/4633/files/The_Lighthouse_Keeper_s_Song.mp3?v=1752471987",
    artworkUrl: "https://cdn.shopify.com/s/files/1/0949/0851/4633/files/the_lighthouse_keeper_keepers_song_artwork.jpg",
  },
];

const occasionKeys: OccasionKey[] = [
  "anniversaries",
  "weddings",
  "birthdays",
  "parents",
  "friends",
  "remembrance",
];

const processStepKeys: ProcessStepKey[] = ["step_1", "step_2", "step_3"];
const includeKeys: IncludeKey[] = ["audio", "lyrics", "artwork", "quality", "delivery", "revisions"];
const faqKeys: FaqKey[] = [
  "home_how",
  "home_delivery",
  "home_revisions",
  "home_includes",
  "product_lyrics",
  "faq_language",
];

export function getLocalizedContent(locale: LocaleCode) {
  const source = locales[locale] || locales[defaultLocale];
  const fallback = locales[defaultLocale];

  return {
    meta: {
      title: "TuneTailor - Custom Songs From Your Story",
      description: text(source.product.lead, fallback.product.lead),
    },
    languageLabel: text(languageLabels[locale], enLocale.localization.language_label),
    nav: {
      home: text(source.navigation.home, fallback.navigation.home),
      signature: text(source.navigation.signature_package, fallback.navigation.signature_package),
      examples: text(source.navigation.examples, fallback.navigation.examples),
      howItWorks: text(source.navigation.how_it_works, fallback.navigation.how_it_works),
      occasions: text(source.navigation.occasions, fallback.navigation.occasions),
      faq: text(source.navigation.faq, fallback.navigation.faq),
      contact: text(source.navigation.contact, fallback.navigation.contact),
      cart: text(source.navigation.cart, fallback.navigation.cart),
    },
    actions: {
      createSong: text(source.actions.create_song, fallback.actions.create_song),
      hearExamples: text(source.actions.hear_songs, fallback.actions.hear_songs),
      addToReview: text(source.product.form.add_review_cart, fallback.product.form.add_review_cart),
      saveSong: extra(locale, "saveSong"),
      cancelEdit: extra(locale, "cancelEdit"),
      edit: extra(locale, "edit"),
      remove: text(source.cart.remove, fallback.cart.remove),
      proceedToCheckout: text(source.cart.checkout, fallback.cart.checkout),
      openingCheckout: extra(locale, "openingCheckout"),
    },
    consent: {
      title: extra(locale, "analyticsTitle"),
      copy: extra(locale, "analyticsCopy"),
      allow: extra(locale, "analyticsAllow"),
      essentialOnly: extra(locale, "analyticsEssentialOnly"),
      choices: extra(locale, "analyticsChoices"),
    },
    hero: {
      title: text(source.hero.title, fallback.hero.title),
      copy: text(source.hero.copy, fallback.hero.copy),
      price: text(source.hero.price, fallback.hero.price),
      imageAlt: text(source.hero.image_alt, fallback.hero.image_alt),
      proof: [
        { value: extra(locale, "twentyFourHours"), label: text(source.product.includes.delivery, fallback.product.includes.delivery) },
        { value: "$49.95", label: text(source.navigation.signature_package, fallback.navigation.signature_package) },
        { value: extra(locale, "thirtyDays"), label: text(source.product.includes.revisions, fallback.product.includes.revisions) },
      ],
    },
    builder: {
      kicker: text(source.navigation.signature_package, fallback.navigation.signature_package),
      title: text(source.product.form.legend, fallback.product.form.legend),
      intro: text(source.product.form.intro, fallback.product.form.intro),
      titleLabel: text(source.product.form.title_label, fallback.product.form.title_label),
      titlePlaceholder: text(source.product.form.title_placeholder, fallback.product.form.title_placeholder),
      genreLabel: text(source.product.form.genre_label, fallback.product.form.genre_label),
      storyLabel: text(source.product.form.story_label, fallback.product.form.story_label),
      storyPlaceholder: text(source.product.form.story_placeholder, fallback.product.form.story_placeholder),
      storyHelp: text(source.product.form.story_help, fallback.product.form.story_help),
      optionalAddOns: text(source.product.addons.legend, fallback.product.addons.legend),
      addOnsIntro: text(source.product.addons.intro, fallback.product.addons.intro),
      englishCopy: text(source.product.english_copy, fallback.product.english_copy),
      genreLabels: {
        "Surprise me": text(source.product.form.genre_surprise, fallback.product.form.genre_surprise),
        Other: text(source.product.form.genre_other, fallback.product.form.genre_other),
      },
      addOns: addOns.reduce(
        (copy, addOn) => {
          const addOnSource = source.product.addons[addOn.key];
          const addOnFallback = fallback.product.addons[addOn.key];
          copy[addOn.key] = {
            label: text(addOnSource.label, addOnFallback.label),
            description: text(addOnSource.description, addOnFallback.description),
          };
          return copy;
        },
        {} as Record<AddOnKey, { label: string; description: string }>,
      ),
    },
    samples: {
      kicker: text(source.audio.eyebrow, fallback.audio.eyebrow),
      title: text(source.audio.title, fallback.audio.title),
      intro: text(source.audio.intro, fallback.audio.intro),
      featured: text(source.audio.featured_label, fallback.audio.featured_label),
      choose: text(source.audio.choose_sample, fallback.audio.choose_sample),
    },
    sampleTracks: sampleAssets.map((sample): SampleTrack => {
      const track = source.audio.tracks[sample.key] || fallback.audio.tracks[sample.key];
      return {
        title: text(track.title, fallback.audio.tracks[sample.key].title),
        occasion: text(track.subtitle, fallback.audio.tracks[sample.key].subtitle),
        description: text(track.description, fallback.audio.tracks[sample.key].description),
        duration: sample.duration,
        audioUrl: sample.audioUrl,
        artworkUrl: sample.artworkUrl,
      };
    }),
    occasions: {
      kicker: text(source.occasions.eyebrow, fallback.occasions.eyebrow),
      title: text(source.occasions.title, fallback.occasions.title),
      intro: text(source.occasions.intro, fallback.occasions.intro),
      items: occasionKeys.map((key) => ({
        title: text(source.occasion_items[key].label, fallback.occasion_items[key].label),
        copy: text(source.occasion_items[key].description, fallback.occasion_items[key].description),
      })),
    },
    process: {
      kicker: text(source.navigation.how_it_works, fallback.navigation.how_it_works),
      title: text(source.process.title, fallback.process.title),
      intro: text(source.process.copy, fallback.process.copy),
      steps: processStepKeys.map((key) => ({
        title: text(source.process.steps[key].title, fallback.process.steps[key].title),
        copy: text(source.process.steps[key].copy, fallback.process.steps[key].copy),
      })),
    },
    packageFeatures: includeKeys.map((key) => text(source.product.includes[key], fallback.product.includes[key])),
    review: {
      kicker: text(source.cart.header_eyebrow, fallback.cart.header_eyebrow),
      title: text(source.cart.header_title, fallback.cart.header_title),
      intro: text(source.cart.note_copy, fallback.cart.note_copy),
      emptyTitle: text(source.cart.empty_title, fallback.cart.empty_title),
      emptyCta: text(source.cart.empty_cta, fallback.cart.empty_cta),
      songLabel: extra(locale, "song"),
      signatureSong: text(source.navigation.signature_package, fallback.navigation.signature_package),
      estimatedTotal: text(source.cart.estimated_total, fallback.cart.estimated_total),
      totalBeforeTaxes: extra(locale, "totalBeforeTaxes"),
      secureCheckout: extra(locale, "secureCheckout"),
    },
    trust: {
      items: [
        {
          title: text(source.product.includes.quality, fallback.product.includes.quality),
          copy: text(source.cart.terms_digital_copy, fallback.cart.terms_digital_copy),
        },
        {
          title: text(source.product.includes.audio, fallback.product.includes.audio),
          copy: text(source.product.includes.artwork, fallback.product.includes.artwork),
        },
        {
          title: text(source.product.english_title, fallback.product.english_title),
          copy: text(source.product.english_copy, fallback.product.english_copy),
        },
      ],
    },
    faq: {
      kicker: text(source.faq.title, fallback.faq.title),
      title: extra(locale, "faqTitle"),
      items: faqKeys.map((key) => ({
        question: text(source.faq.items[key].question, fallback.faq.items[key].question),
        answer: plainText(source.faq.items[key].answer_html, fallback.faq.items[key].answer_html),
      })),
    },
    footer: {
      tagline: text(source.final_cta.title, fallback.final_cta.title),
      store: extra(locale, "store"),
    },
    status: {
      syncing: extra(locale, "syncing"),
      storefrontApi: extra(locale, "storefrontApi"),
      snapshot: extra(locale, "snapshot"),
    },
    errors: {
      titleRequired: extra(locale, "titleRequired"),
      genreInvalid: extra(locale, "genreInvalid"),
      otherGenre: text(source.product.form.error_other, fallback.product.form.error_other),
      storyRequired: extra(locale, "storyRequired"),
      checkoutFailed: extra(locale, "checkoutFailed"),
    },
  };
}

function text(value: string | undefined | null, fallback: string): string {
  return value || fallback;
}

function plainText(value: string | undefined | null, fallback: string): string {
  return text(value, fallback).replace(/<\/?p>/g, "").trim();
}

type ExtraCopyKey =
  | "analyticsAllow"
  | "analyticsCopy"
  | "analyticsChoices"
  | "analyticsEssentialOnly"
  | "analyticsTitle"
  | "cancelEdit"
  | "checkoutFailed"
  | "edit"
  | "faqTitle"
  | "genreInvalid"
  | "openingCheckout"
  | "saveSong"
  | "secureCheckout"
  | "snapshot"
  | "song"
  | "store"
  | "storyRequired"
  | "storefrontApi"
  | "syncing"
  | "thirtyDays"
  | "titleRequired"
  | "twentyFourHours"
  | "totalBeforeTaxes";

const extraCopy: Record<LocaleCode, Record<ExtraCopyKey, string>> = {
  en: {
    analyticsAllow: "Allow analytics",
    analyticsCopy: "Optional, privacy-limited analytics help us improve this storefront. Your song title and story are never included.",
    analyticsChoices: "Privacy choices",
    analyticsEssentialOnly: "Essential only",
    analyticsTitle: "Your privacy",
    cancelEdit: "Cancel edit",
    checkoutFailed: "Checkout could not start.",
    edit: "Edit",
    faqTitle: "Before you checkout",
    genreInvalid: "Choose a valid genre.",
    openingCheckout: "Opening checkout",
    saveSong: "Save song",
    secureCheckout: "Secure Shopify checkout. Taxes, discounts, and order status stay with production Shopify.",
    snapshot: "Live Shopify snapshot",
    song: "Song",
    store: "Store",
    storyRequired: "Complete the Story or lyrics brief before review.",
    storefrontApi: "Live Shopify catalog",
    syncing: "Syncing live Shopify prices",
    thirtyDays: "30 days",
    titleRequired: "Add a song title before review.",
    twentyFourHours: "24 hours",
    totalBeforeTaxes: "Total before taxes",
  },
  es: {
    analyticsAllow: "Permitir analítica",
    analyticsCopy: "La analítica opcional y limitada ayuda a mejorar esta tienda. El título y la historia de tu canción nunca se incluyen.",
    analyticsChoices: "Opciones de privacidad",
    analyticsEssentialOnly: "Solo esenciales",
    analyticsTitle: "Tu privacidad",
    cancelEdit: "Cancelar edición",
    checkoutFailed: "No se pudo iniciar el pago.",
    edit: "Editar",
    faqTitle: "Antes de pagar",
    genreInvalid: "Elige un género válido.",
    openingCheckout: "Abriendo el pago",
    saveSong: "Guardar canción",
    secureCheckout: "Pago seguro con Shopify. Impuestos, descuentos y estado del pedido permanecen en Shopify de producción.",
    snapshot: "Snapshot activo de Shopify",
    song: "Canción",
    store: "Tienda",
    storyRequired: "Completa el brief de Story or lyrics antes de revisar.",
    storefrontApi: "Catálogo activo de Shopify",
    syncing: "Sincronizando precios activos de Shopify",
    thirtyDays: "30 días",
    titleRequired: "Añade un título de canción antes de revisar.",
    twentyFourHours: "24 horas",
    totalBeforeTaxes: "Total antes de impuestos",
  },
  fr: {
    analyticsAllow: "Autoriser l'analyse",
    analyticsCopy: "Des mesures facultatives et limitées nous aident à améliorer cette boutique. Le titre et l'histoire de votre chanson ne sont jamais inclus.",
    analyticsChoices: "Choix de confidentialité",
    analyticsEssentialOnly: "Essentiel uniquement",
    analyticsTitle: "Votre vie privée",
    cancelEdit: "Annuler la modification",
    checkoutFailed: "Le paiement n'a pas pu démarrer.",
    edit: "Modifier",
    faqTitle: "Avant le paiement",
    genreInvalid: "Choisissez un genre valide.",
    openingCheckout: "Ouverture du paiement",
    saveSong: "Enregistrer la chanson",
    secureCheckout: "Paiement sécurisé Shopify. Taxes, réductions et statut de commande restent dans Shopify de production.",
    snapshot: "Instantané Shopify en direct",
    song: "Chanson",
    store: "Boutique",
    storyRequired: "Complétez le brief Story or lyrics avant la vérification.",
    storefrontApi: "Catalogue Shopify en direct",
    syncing: "Synchronisation des prix Shopify en direct",
    thirtyDays: "30 jours",
    titleRequired: "Ajoutez un titre de chanson avant la vérification.",
    twentyFourHours: "24 heures",
    totalBeforeTaxes: "Total avant taxes",
  },
  it: {
    analyticsAllow: "Consenti analisi",
    analyticsCopy: "Analisi facoltative e limitate ci aiutano a migliorare il negozio. Il titolo e la storia della canzone non vengono mai inclusi.",
    analyticsChoices: "Scelte sulla privacy",
    analyticsEssentialOnly: "Solo essenziali",
    analyticsTitle: "La tua privacy",
    cancelEdit: "Annulla modifica",
    checkoutFailed: "Impossibile avviare il checkout.",
    edit: "Modifica",
    faqTitle: "Prima del checkout",
    genreInvalid: "Scegli un genere valido.",
    openingCheckout: "Apertura checkout",
    saveSong: "Salva canzone",
    secureCheckout: "Checkout Shopify sicuro. Imposte, sconti e stato dell'ordine restano nello Shopify di produzione.",
    snapshot: "Snapshot Shopify live",
    song: "Canzone",
    store: "Negozio",
    storyRequired: "Completa il brief Story or lyrics prima della revisione.",
    storefrontApi: "Catalogo Shopify live",
    syncing: "Sincronizzazione prezzi Shopify live",
    thirtyDays: "30 giorni",
    titleRequired: "Aggiungi un titolo prima della revisione.",
    twentyFourHours: "24 ore",
    totalBeforeTaxes: "Totale prima delle imposte",
  },
  de: {
    analyticsAllow: "Analyse erlauben",
    analyticsCopy: "Optionale, begrenzte Analysen helfen uns, diesen Shop zu verbessern. Songtitel und Geschichte werden nie einbezogen.",
    analyticsChoices: "Datenschutzauswahl",
    analyticsEssentialOnly: "Nur erforderlich",
    analyticsTitle: "Deine Privatsphäre",
    cancelEdit: "Bearbeitung abbrechen",
    checkoutFailed: "Checkout konnte nicht gestartet werden.",
    edit: "Bearbeiten",
    faqTitle: "Vor dem Checkout",
    genreInvalid: "Wähle ein gültiges Genre.",
    openingCheckout: "Checkout wird geöffnet",
    saveSong: "Song speichern",
    secureCheckout: "Sicherer Shopify-Checkout. Steuern, Rabatte und Bestellstatus bleiben im Produktions-Shopify.",
    snapshot: "Live-Shopify-Snapshot",
    song: "Song",
    store: "Shop",
    storyRequired: "Vervollständige den Story-or-lyrics-Brief vor der Prüfung.",
    storefrontApi: "Live-Shopify-Katalog",
    syncing: "Live-Shopify-Preise werden synchronisiert",
    thirtyDays: "30 Tage",
    titleRequired: "Füge vor der Prüfung einen Songtitel hinzu.",
    twentyFourHours: "24 Stunden",
    totalBeforeTaxes: "Summe vor Steuern",
  },
  ja: {
    analyticsAllow: "分析を許可",
    analyticsCopy: "任意の限定的な分析はストアの改善に役立ちます。曲名やストーリーは送信されません。",
    analyticsChoices: "プライバシー設定",
    analyticsEssentialOnly: "必須のみ",
    analyticsTitle: "プライバシー",
    cancelEdit: "編集をキャンセル",
    checkoutFailed: "チェックアウトを開始できませんでした。",
    edit: "編集",
    faqTitle: "チェックアウト前に",
    genreInvalid: "有効なジャンルを選択してください。",
    openingCheckout: "チェックアウトを開いています",
    saveSong: "曲を保存",
    secureCheckout: "安全なShopifyチェックアウトです。税金、割引、注文ステータスは本番Shopifyで管理されます。",
    snapshot: "Shopifyライブスナップショット",
    song: "曲",
    store: "ストア",
    storyRequired: "確認前にStory or lyricsのブリーフを入力してください。",
    storefrontApi: "Shopifyライブカタログ",
    syncing: "Shopifyのライブ価格を同期中",
    thirtyDays: "30日",
    titleRequired: "確認前に曲のタイトルを入力してください。",
    twentyFourHours: "24時間",
    totalBeforeTaxes: "税抜合計",
  },
};

function extra(locale: LocaleCode, key: ExtraCopyKey): string {
  return extraCopy[locale]?.[key] || extraCopy[defaultLocale][key];
}
